import { useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../../context/AuthContext'
import PersonaAvatar from '../../components/PersonaAvatar'
import { useMusicStore } from '../../components/MusicPlayer'

interface BioData {
  tagline?: string
  statement?: string[]
  hero_image?: string
  releases?: { title: string; year: string; streams: number; desc: string; art: string }[]
  influences?: string[]
  connect_links?: Record<string, string>
}

export default function CommunityBioPage() {
  const { user } = useAuth()
  const slug = user?.communitySlug || ''
  const communityId = user?.communityId || `portal-${slug}`
  const token = (localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1])
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null)

  const { data: bioData } = useQuery({
    queryKey: ['communityBio', communityId],
    queryFn: async () => {
      const res = await fetch(`/api/communities/${communityId}/objects?type=bio`, { headers })
      if (!res.ok) return null
      const arr = await res.json()
      const obj = Array.isArray(arr) ? arr[0] : arr
      if (!obj) return null
      return (typeof obj.data === 'string' ? JSON.parse(obj.data) : obj.data) as BioData
    },
    enabled: !!communityId,
  })

  // Fetch music tracks for player integration
  const { data: musicTracks } = useQuery({
    queryKey: ['communityMusic', communityId],
    queryFn: async () => {
      const res = await fetch(`/api/communities/${communityId}/objects?type=music_track`, { headers })
      if (!res.ok) return []
      const arr = await res.json()
      return (Array.isArray(arr) ? arr : []).map((o: Record<string, unknown>) => {
        const d = typeof o.data === 'string' ? JSON.parse(o.data as string) : o.data
        return { ...d, id: o.id }
      })
    },
    enabled: !!communityId,
  })

  // Fetch community team
  const { data: teamNames } = useQuery({
    queryKey: ['communityTeam', communityId],
    queryFn: async () => {
      const res = await fetch(`/api/communities/${communityId}/personas`, { headers })
      if (!res.ok) return []
      const data = await res.json()
      return data.personas || data || []
    },
    enabled: !!communityId,
  })

  // Hero background transparency
  const heroRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (bioData?.hero_image) {
      const rootEl = document.getElementById('root')
      const mainEl = heroRef.current?.closest('main')
      if (rootEl) rootEl.style.background = 'transparent'
      if (mainEl) mainEl.style.background = 'transparent'
      document.body.style.setProperty('background', 'transparent', 'important')
      document.documentElement.style.background = 'transparent'
      return () => {
        if (rootEl) rootEl.style.background = ''
        if (mainEl) mainEl.style.background = ''
        document.body.style.removeProperty('background')
        document.documentElement.style.background = ''
      }
    }
  }, [bioData?.hero_image])

  function playRelease(index: number) {
    if (!musicTracks?.length) return
    useMusicStore.getState().setPlaylist(musicTracks.map((t: Record<string, unknown>) => ({
      title: String(t.title || ''), artist: String(t.artist || ''), scUrl: String(t.sc_url || ''), art: String(t.art || ''),
    })))
    useMusicStore.getState().playTrack(index)
  }

  const communityName = user?.communityName || 'Artist'

  if (!bioData) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Bio coming soon</p>
      </div>
    )
  }

  return (
    <div ref={heroRef} className="relative">
      {/* Hero */}
      {bioData.hero_image && (
        <div className="relative overflow-hidden" style={{ height: 360 }}>
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${bioData.hero_image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center 20%',
            }}
          />
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(to bottom, rgba(10,22,40,0.2) 0%, rgba(10,22,40,0.5) 40%, rgba(10,22,40,0.95) 85%, rgba(10,22,40,1) 100%)',
          }} />
          <div className="absolute bottom-0 left-0 right-0 p-8 max-w-3xl mx-auto" style={{ zIndex: 2 }}>
            <h1 className="text-4xl font-extrabold tracking-tight" style={{ color: '#fff', textShadow: '0 2px 24px rgba(0,0,0,0.5)' }}>
              {communityName}
            </h1>
            {bioData.tagline && (
              <div className="text-base font-medium italic mt-2" style={{ color: 'var(--accent)', letterSpacing: 0.5 }}>
                {bioData.tagline}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto px-8 pb-24" style={{ position: 'relative', zIndex: 1 }}>
        {/* Artist Statement */}
        {bioData.statement && (
          <section className="mt-12">
            <h2 className="text-xs font-bold uppercase tracking-widest mb-5" style={{ color: 'var(--accent)' }}>About</h2>
            <div className="text-base leading-relaxed" style={{ color: 'var(--text-primary)' }}>
              {bioData.statement.map((p, i) => (
                <p key={i} className="mb-4">{p}</p>
              ))}
            </div>
          </section>
        )}

        {/* Photo Divider */}
        {bioData.releases && (
          <div className="grid grid-cols-3 gap-2 mt-10">
            {['/uploads/bianca-breakus-cover.jpg', '/uploads/bianca-spotify-artist.jpg', '/uploads/bianca-profile-deezer.jpg'].map((src, i) => (
              <img
                key={i}
                src={src}
                alt=""
                className="rounded-lg object-cover aspect-video cursor-pointer hover:scale-105 transition-transform"
                style={{ width: '100%' }}
                onClick={() => setLightboxSrc(src)}
              />
            ))}
          </div>
        )}

        {/* Releases */}
        {bioData.releases && bioData.releases.length > 0 && (
          <section className="mt-12">
            <h2 className="text-xs font-bold uppercase tracking-widest mb-5" style={{ color: 'var(--accent)' }}>Releases</h2>
            <div className="flex flex-col gap-3">
              {bioData.releases.map((r, i) => (
                <button
                  key={i}
                  onClick={() => playRelease(i)}
                  className="flex items-center gap-4 px-5 py-4 rounded-xl text-left transition-colors hover:bg-[var(--bg-elevated)] group"
                  style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
                >
                  <img src={r.art} alt={r.title} className="w-14 h-14 rounded-lg object-cover shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                      {r.title} <span className="font-normal text-xs" style={{ color: 'var(--text-secondary)' }}>({r.year})</span>
                    </div>
                    <div className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>{r.desc}</div>
                    <div className="text-xs mt-1 font-medium" style={{ color: 'var(--accent)' }}>{r.streams.toLocaleString()} streams</div>
                  </div>
                  <span className="text-lg opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--accent)' }}>▶</span>
                </button>
              ))}
            </div>
            <div className="text-center text-xs mt-3" style={{ color: 'var(--text-secondary)' }}>
              <strong style={{ color: 'var(--accent)' }}>89,508</strong> total streams across {bioData.releases.length} releases
            </div>
          </section>
        )}

        {/* Mission Quote */}
        <section className="mt-12">
          <div className="rounded-xl p-8 text-center" style={{ background: 'rgba(17,29,46,0.8)', border: '1px solid rgba(212,160,84,0.15)' }}>
            <div className="text-lg font-semibold italic leading-relaxed" style={{ color: 'var(--text-primary)' }}>
              "Get my music to as many people as possible."
            </div>
            <div className="text-xs mt-3" style={{ color: 'var(--accent)' }}>That is why we are here.</div>
          </div>
        </section>

        {/* Influences */}
        {bioData.influences && (
          <section className="mt-12">
            <h2 className="text-xs font-bold uppercase tracking-widest mb-5" style={{ color: 'var(--accent)' }}>Influences & Orbit</h2>
            <div className="flex flex-wrap gap-2">
              {bioData.influences.map((name, i) => (
                <span
                  key={i}
                  className="text-xs px-3 py-1.5 rounded-full"
                  style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
                >
                  {name}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Connect */}
        {bioData.connect_links && (
          <section className="mt-12">
            <h2 className="text-xs font-bold uppercase tracking-widest mb-5" style={{ color: 'var(--accent)' }}>Connect</h2>
            <div className="flex flex-wrap gap-3">
              {Object.entries(bioData.connect_links).map(([platform, url]) => (
                <a
                  key={platform}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-semibold px-4 py-2 rounded-lg no-underline transition-colors hover:border-[var(--accent)]"
                  style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
                >
                  {platform.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </a>
              ))}
            </div>
          </section>
        )}

        {/* Team */}
        {teamNames && teamNames.length > 0 && (
          <section className="mt-12">
            <h2 className="text-xs font-bold uppercase tracking-widest mb-5" style={{ color: 'var(--accent)' }}>Your Team</h2>
            <div className="flex gap-4">
              {teamNames.map((name: string) => (
                <div key={name} className="flex items-center gap-2">
                  <PersonaAvatar name={name} size={28} />
                  <span className="text-sm font-medium capitalize" style={{ color: 'var(--text-primary)' }}>{name}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Lightbox */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(8px)' }}
          onClick={() => setLightboxSrc(null)}
        >
          <button
            className="absolute top-5 right-6 text-2xl"
            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', opacity: 0.7 }}
            onClick={() => setLightboxSrc(null)}
          >
            ✕
          </button>
          <img src={lightboxSrc} alt="" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 8, boxShadow: '0 8px 48px rgba(0,0,0,0.6)' }} />
        </div>
      )}
    </div>
  )
}
